import posthog from "posthog-js";
import type { SessionData } from "@app-types";

/** Match `index.tsx`: only skip when PostHog was never initialized. */
function isPosthogActive(): boolean {
    return (
        typeof window !== `undefined` &&
        Boolean(window.settings?.POSTHOG_API_KEY) &&
        Boolean(posthog.__loaded)
    );
}

/** Stable key from merged session — for effect deps (logout vs logged-in, account/community changes). */
export function identityKeyForSession(session: SessionData | null): string {
    if (!session) return `anon`;
    return `full:${session.idPayor}:${session.email}:${session.idAccount}:${session.idCommunity}`;
}

/** Dedupe: same payor only needs one identify per browser session until logout. */
let lastIdentifiedPayorId: string | null = null;

/**
 * Sets distinct id, person properties, and super properties from merged session context.
 * Invoked from PosthogIdentifyProvider. `identify` and `setPersonProperties` are deduped per
 * payor until logout; `register` always runs so super properties stay current if account or
 * community context changes within the same session.
 */
export function identifyPosthogUser(session: SessionData): void {
    if (!isPosthogActive()) return;

    const distinctId = String(session.idPayor);
    /* eslint-disable camelcase -- PostHog person/super property names */
    const ids = {
        user_id: String(session.idPayor),
        payor_id: String(session.idPayor),
        account_id: String(session.idAccount),
        community_id: String(session.idCommunity)
    };
    /* eslint-enable camelcase */

    if (lastIdentifiedPayorId !== distinctId) {
        posthog.identify(distinctId, {
            email: session.email,
            $email: session.email
        });
        posthog.setPersonProperties(ids);
        lastIdentifiedPayorId = distinctId;
    }

    posthog.register({
        environment: window.settings.ENV,
        site: window.settings.POSTHOG_SITE_ID,
        ...ids
    });
}

export function resetPosthogOnLogout(): void {
    if (!isPosthogActive()) return;
    lastIdentifiedPayorId = null;
    posthog.reset();
    posthog.register({
        environment: window.settings.ENV,
        site: window.settings.POSTHOG_SITE_ID
    });
}
